import type { Directive, DirectiveBinding } from 'vue'
import { usePermissionsStore } from '@/stores'
import type { PermissionCheckInput } from '@/types/rbac'

export interface PermissionDirectiveValue {
  permission: string
  domainId?: number
  mode?: 'hide' | 'disable'
}

type PermissionValue = string | PermissionCheckInput | PermissionDirectiveValue

function normalizeValue(value: PermissionValue): PermissionDirectiveValue {
  if (typeof value === 'string') {
    return { permission: value, mode: 'hide' }
  }
  return {
    permission: value.permission,
    domainId: value.domainId,
    mode: value.mode ?? 'hide'
  }
}

function checkPermission(value: PermissionDirectiveValue): boolean {
  const permissionsStore = usePermissionsStore()

  if (value.domainId !== undefined) {
    return permissionsStore.hasDomainPermission(value.domainId, value.permission)
  }

  return permissionsStore.hasPermission(value.permission)
}

function updateElement(el: HTMLElement, binding: DirectiveBinding<PermissionValue>) {
  const value = normalizeValue(binding.value)
  const hasPermission = checkPermission(value)

  if (hasPermission) {
    el.style.display = ''
    if (el instanceof HTMLButtonElement || el instanceof HTMLInputElement) {
      el.disabled = false
    }
    el.removeAttribute('aria-disabled')
    el.classList.remove('permission-disabled')
  } else {
    if (value.mode === 'disable') {
      if (el instanceof HTMLButtonElement || el instanceof HTMLInputElement) {
        el.disabled = true
      }
      el.setAttribute('aria-disabled', 'true')
      el.classList.add('permission-disabled')
    } else {
      el.style.display = 'none'
    }
  }
}

export const vPermission: Directive<HTMLElement, PermissionValue> = {
  mounted(el, binding) {
    updateElement(el, binding)
  },
  updated(el, binding) {
    updateElement(el, binding)
  }
}

export default vPermission
